import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { dashboardAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  Building2,
  Home,
  Users,
  FileText,
  Euro,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Loader2
} from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const statCards = [
    {
      title: 'Immobilien',
      value: stats?.properties_count || 0,
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      link: '/properties'
    },
    {
      title: 'Einheiten',
      value: stats?.units_count || 0,
      icon: Home,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      link: '/units'
    },
    {
      title: 'Aktive Mieter',
      value: stats?.active_tenants || 0,
      icon: Users,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      link: '/tenants'
    },
    {
      title: 'Gesamtkosten',
      value: formatCurrency(stats?.total_costs || 0),
      icon: Euro,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      link: '/costs'
    }
  ];

  return (
    <Layout>
      <div data-testid="dashboard-page" className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Übersicht Ihrer Immobilienverwaltung</p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Link key={stat.title} to={stat.link}>
              <Card 
                className="hover:shadow-md transition-shadow cursor-pointer border-slate-200"
                data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">{stat.title}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                        {stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                      <stat.icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Billings Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Billing Stats */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-400" />
                Abrechnungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Gesamt</span>
                <span className="font-semibold text-slate-900">{stats?.billings_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <span className="text-sm text-amber-700">Entwürfe</span>
                <span className="font-semibold text-amber-700">{stats?.draft_billings || 0}</span>
              </div>
              <Link to="/billings">
                <Button variant="outline" className="w-full mt-2" data-testid="view-billings-button">
                  Alle anzeigen
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Recent Billings */}
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Letzte Abrechnungen</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.recent_billings?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_billings.map((billing) => (
                    <div 
                      key={billing.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {billing.tenant_name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {formatDate(billing.period_start)} - {formatDate(billing.period_end)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className={`flex items-center gap-1 font-semibold tabular-nums ${
                            billing.balance > 0 ? 'text-red-600' : 'text-emerald-600'
                          }`}>
                            {billing.balance > 0 ? (
                              <TrendingUp className="h-4 w-4" />
                            ) : (
                              <TrendingDown className="h-4 w-4" />
                            )}
                            {formatCurrency(Math.abs(billing.balance))}
                          </div>
                          <p className="text-xs text-slate-500">
                            {billing.balance > 0 ? 'Nachzahlung' : 'Guthaben'}
                          </p>
                        </div>
                        <Badge 
                          variant={billing.status === 'final' ? 'default' : 'secondary'}
                          className={billing.status === 'final' 
                            ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' 
                            : 'bg-slate-100 text-slate-600'
                          }
                        >
                          {billing.status === 'final' ? 'Final' : 'Entwurf'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Keine Abrechnungen vorhanden</p>
                  <Link to="/billings">
                    <Button variant="outline" className="mt-4" data-testid="create-first-billing-button">
                      Erste Abrechnung erstellen
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Schnellaktionen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/properties">
                <Button data-testid="quick-add-property">
                  <Building2 className="mr-2 h-4 w-4" />
                  Immobilie hinzufügen
                </Button>
              </Link>
              <Link to="/costs">
                <Button variant="outline" data-testid="quick-add-cost">
                  <Euro className="mr-2 h-4 w-4" />
                  Kosten erfassen
                </Button>
              </Link>
              <Link to="/billings">
                <Button variant="outline" data-testid="quick-create-billing">
                  <FileText className="mr-2 h-4 w-4" />
                  Abrechnung erstellen
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
